import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import SprintIcon from '@atlaskit/icon/core/sprint';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { EntityValueGrid, EntityValueGridRow } from '../../common/ui/entity-value-grid';
import { formatDate } from '../../services/utils';

import { messages } from './messages';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
});

const sprintIcon = <IconTile icon={SprintIcon} label="" appearance="gray" size="small" />;

export const JiraCreateSprintAction = ({
	actions,
	getReadonly,
	sendMessageConfirmedTools,
	conversationId,
	messageId,
	recipientAgentId,
}: ActionRendererFor<typeof ActionKeys.JiraCreateSprint>): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const action = actions[0];

	const { data } = action;
	const readonly = getReadonly(action);

	const [isActioned, setIsActioned] = useState(false);
	const buttonsDisabled = isActioned || readonly;

	const handleAction = useCallback(
		(resolutionType: 'confirmed' | 'cancelled') => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId:
					resolutionType === 'confirmed' ? 'confirmCreateSprintButton' : 'cancelCreateSprintButton',
			});
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: 'jiraCreateSprintAction' },
				confirmed_tools: {
					message_id: messageId,
					tools: [
						{
							key: action.key,
							tool_id: action.actionId,
							tool_source: action.toolSource,
							invocation_id: action.invocationId,
							resolution: { type: resolutionType },
							data: { human_message_id: action.humanMessageId ?? '', arguments: action.data },
						},
					],
				},
				resolutionType,
			});
			setIsActioned(true);
		},
		[
			sendAnalyticsEvent,
			sendMessageConfirmedTools,
			recipientAgentId,
			conversationId,
			messageId,
			action,
		],
	);

	const handleConfirm = useCallback(() => handleAction('confirmed'), [handleAction]);
	const handleCancel = useCallback(() => handleAction('cancelled'), [handleAction]);

	return (
		<ActionCardWrapper title={formatMessage(messages.title)} headerIcon={sprintIcon}>
			<EntityValueGrid>
				<EntityValueGridRow label={formatMessage(messages.nameLabel)} value={data.name} />
				{data.goal && (
					<EntityValueGridRow label={formatMessage(messages.goalLabel)} value={data.goal} />
				)}
				{data.startDate && (
					<EntityValueGridRow
						label={formatMessage(messages.startDateLabel)}
						value={formatDate(data.startDate)}
					/>
				)}
				{data.endDate && (
					<EntityValueGridRow
						label={formatMessage(messages.endDateLabel)}
						value={formatDate(data.endDate)}
					/>
				)}
			</EntityValueGrid>

			<ActionCardFooter>
				<Box xcss={styles.footer}>
					<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
						{formatMessage(messages.cancelButton)}
					</Button>
					<Button appearance="primary" onClick={handleConfirm} isDisabled={buttonsDisabled}>
						{formatMessage(messages.confirmButton)}
					</Button>
				</Box>
			</ActionCardFooter>
		</ActionCardWrapper>
	);
};
